import React, { useState, useEffect } from 'react';
import api from './config/api';
import InventoryList from './components/InventoryList';
import InventoryDetail from './components/InventoryDetail';
import CreateInventoryModal from './components/CreateInventoryModal';
import ItemTypeList from './components/ItemTypeList';
import CreateItemTypeModal from './components/CreateItemTypeModal';
import LocationTypeList from './components/LocationTypeList';
import CreateLocationTypeModal from './components/CreateLocationTypeModal';
import AIConfiguration from './components/AIConfiguration';
import CompletionAgentConfiguration from './components/CompletionAgentConfiguration';
import BacklogList from './components/BacklogList';
import EditInventoryItemTypesModal from './components/EditInventoryItemTypesModal';

function App() {
  const [inventories, setInventories] = useState([]);
  const [itemTypes, setItemTypes] = useState([]);
  const [locationTypes, setLocationTypes] = useState([]);
  const [selectedInventory, setSelectedInventory] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showCreateTypeModal, setShowCreateTypeModal] = useState(false);
  const [showCreateLocationTypeModal, setShowCreateLocationTypeModal] = useState(false);
  const [editingItemType, setEditingItemType] = useState(null);
  const [editingLocationType, setEditingLocationType] = useState(null);
  const [editingInventoryItemTypes, setEditingInventoryItemTypes] = useState(null);
  const [currentView, setCurrentView] = useState('inventories'); // 'inventories', 'types', 'locations', 'backlog', 'ai-config', or 'completion-agents'
  const [showAdminDropdown, setShowAdminDropdown] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInventories();
    fetchItemTypes();
    fetchLocationTypes();
  }, []);

  const fetchInventories = async () => {
    try {
      const response = await api.get('/inventories');
      setInventories(response.data);
      return response.data;
    } catch (error) {
      console.error('Error fetching inventories:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const fetchItemTypes = async () => {
    try {
      const response = await api.get('/item-types');
      setItemTypes(response.data);
    } catch (error) {
      console.error('Error fetching item types:', error);
    }
  };

  const fetchLocationTypes = async () => {
    try {
      const response = await api.get('/location-types');
      setLocationTypes(response.data);
    } catch (error) {
      console.error('Error fetching location types:', error);
    }
  };

  const createInventory = async (inventoryData) => {
    try {
      console.log('Creating inventory with data:', inventoryData);
      const response = await api.post('/inventories', inventoryData);
      console.log('Create inventory response:', response.data);
      setInventories([...inventories, response.data]);
      setShowCreateModal(false);
    } catch (error) {
      console.error('Error creating inventory:', error);
      console.error('Error details:', error.response?.data);
      alert('Error creating inventory: ' + (error.response?.data?.error || error.message));
    }
  };

  const deleteInventory = async (id) => {
    try {
      await api.delete(`/inventories/${id}`);
      setInventories(inventories.filter(inv => inv.id !== id));
      if (selectedInventory && selectedInventory.id === id) {
        setSelectedInventory(null);
      }
    } catch (error) {
      console.error('Error deleting inventory:', error);
    }
  };

  const createItemType = async (itemTypeData) => {
    try {
      console.log('Creating item type with data:', itemTypeData);
      const response = await api.post('/item-types', itemTypeData);
      console.log('Create item type response:', response.data);
      setItemTypes([...itemTypes, response.data]);
      setShowCreateTypeModal(false);
      setEditingItemType(null);
    } catch (error) {
      console.error('Error creating item type:', error);
      console.error('Error details:', error.response?.data);
      alert('Error creating item type: ' + (error.response?.data?.error || error.message));
    }
  };

  const updateItemType = async (itemTypeData) => {
    try {
      console.log('Updating item type with data:', itemTypeData);
      const response = await api.put(`/item-types/${editingItemType.id}`, itemTypeData);
      console.log('Update item type response:', response.data);
      setItemTypes(itemTypes.map(type => 
        type.id === editingItemType.id ? response.data : type
      ));
      
      // Refresh inventories to get updated item type data
      const refreshedInventories = await fetchInventories();
      
      // If we have a selected inventory, refresh it with updated data
      if (selectedInventory) {
        const updatedSelectedInventory = refreshedInventories.find(inv => inv.id === selectedInventory.id);
        if (updatedSelectedInventory) {
          setSelectedInventory(updatedSelectedInventory);
        }
      }
      
      setShowCreateTypeModal(false);
      setEditingItemType(null);
    } catch (error) {
      console.error('Error updating item type:', error);
      console.error('Error details:', error.response?.data);
      alert('Error updating item type: ' + (error.response?.data?.error || error.message));
    }
  };

  const deleteItemType = async (id) => {
    try {
      await api.delete(`/item-types/${id}`);
      setItemTypes(itemTypes.filter(type => type.id !== id));
    } catch (error) {
      console.error('Error deleting item type:', error);
      alert('Error deleting item type: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleItemTypeAction = (itemTypeData) => {
    if (editingItemType) {
      updateItemType(itemTypeData);
    } else {
      createItemType(itemTypeData);
    }
  };

  const createLocationType = async (locationTypeData) => {
    try {
      console.log('Creating location type with data:', locationTypeData);
      const response = await api.post('/location-types', locationTypeData);
      console.log('Create location type response:', response.data);
      setLocationTypes([...locationTypes, response.data]);
      setShowCreateLocationTypeModal(false);
      setEditingLocationType(null);
    } catch (error) {
      console.error('Error creating location type:', error);
      console.error('Error details:', error.response?.data);
      alert('Error creating location type: ' + (error.response?.data?.error || error.message));
    }
  };

  const updateLocationType = async (locationTypeData) => {
    try {
      console.log('Updating location type with data:', locationTypeData);
      const response = await api.put(`/location-types/${editingLocationType.id}`, locationTypeData);
      console.log('Update location type response:', response.data);
      setLocationTypes(locationTypes.map(type => 
        type.id === editingLocationType.id ? response.data : type
      ));
      setShowCreateLocationTypeModal(false);
      setEditingLocationType(null);
    } catch (error) {
      console.error('Error updating location type:', error);
      console.error('Error details:', error.response?.data);
      alert('Error updating location type: ' + (error.response?.data?.error || error.message));
    }
  };

  const deleteLocationType = async (id) => {
    try {
      await api.delete(`/location-types/${id}`);
      setLocationTypes(locationTypes.filter(type => type.id !== id));
    } catch (error) {
      console.error('Error deleting location type:', error);
      alert('Error deleting location type: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleLocationTypeAction = (locationTypeData) => {
    if (editingLocationType) {
      updateLocationType(locationTypeData);
    } else {
      createLocationType(locationTypeData);
    }
  };

  if (loading) {
    return <div className="container">Loading...</div>;
  }

  return (
    <div>
      <div className="header">
        <h1>Inventorium</h1>
        <nav style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <button 
            className={`btn ${currentView === 'inventories' ? 'btn-active' : ''}`}
            onClick={() => {
              setCurrentView('inventories');
              setSelectedInventory(null);
              setShowAdminDropdown(false);
            }}
            style={{ marginRight: '1rem' }}
          >
            Inventories
          </button>
          <button 
            className={`btn ${currentView === 'types' ? 'btn-active' : ''}`}
            onClick={() => {
              setCurrentView('types');
              setSelectedInventory(null);
              setShowAdminDropdown(false);
            }}
            style={{ marginRight: '1rem' }}
          >
            Item Types
          </button>
          <button 
            className={`btn ${currentView === 'locations' ? 'btn-active' : ''}`}
            onClick={() => {
              setCurrentView('locations');
              setSelectedInventory(null);
              setShowAdminDropdown(false);
            }}
            style={{ marginRight: '1rem' }}
          >
            Location Types
          </button>
          
          {/* Admin dropdown */}
          <div style={{ position: 'relative', marginRight: '1rem' }}>
            <button 
              className={`btn ${['backlog', 'ai-config', 'completion-agents'].includes(currentView) ? 'btn-active' : ''}`}
              onClick={() => setShowAdminDropdown(!showAdminDropdown)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
            >
              Admin {showAdminDropdown ? '▲' : '▼'}
            </button>
            
            {showAdminDropdown && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                backgroundColor: 'white',
                border: '1px solid #ddd',
                borderRadius: '4px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                zIndex: 1000,
                minWidth: '200px',
                marginTop: '4px'
              }}>
                <button 
                  className={`btn ${currentView === 'backlog' ? 'btn-active' : ''}`}
                  onClick={() => {
                    setCurrentView('backlog');
                    setSelectedInventory(null);
                    setShowAdminDropdown(false);
                  }}
                  style={{ 
                    width: '100%', 
                    textAlign: 'left', 
                    border: 'none',
                    borderRadius: '0',
                    padding: '0.75rem 1rem'
                  }}
                >
                  📋 Backlog
                </button>
                <button 
                  className={`btn ${currentView === 'ai-config' ? 'btn-active' : ''}`}
                  onClick={() => {
                    setCurrentView('ai-config');
                    setSelectedInventory(null);
                    setShowAdminDropdown(false);
                  }}
                  style={{ 
                    width: '100%', 
                    textAlign: 'left', 
                    border: 'none',
                    borderRadius: '0',
                    padding: '0.75rem 1rem'
                  }}
                >
                  🤖 AI Configuration
                </button>
                <button 
                  className={`btn ${currentView === 'completion-agents' ? 'btn-active' : ''}`}
                  onClick={() => {
                    setCurrentView('completion-agents');
                    setSelectedInventory(null);
                    setShowAdminDropdown(false);
                  }}
                  style={{ 
                    width: '100%', 
                    textAlign: 'left', 
                    border: 'none',
                    borderRadius: '0',
                    padding: '0.75rem 1rem'
                  }}
                >
                  🔗 Completion Agents
                </button>
              </div>
            )}
          </div>
        </nav>
      </div>
      <div className="container">
        {selectedInventory ? (
          <InventoryDetail
            inventory={selectedInventory}
            onBack={() => setSelectedInventory(null)}
            onUpdate={(updated) => {
              setSelectedInventory(updated);
              setInventories(inventories.map(inv => 
                inv.id === updated.id ? updated : inv
              ));
            }}
          />
        ) : currentView === 'inventories' ? (
          <InventoryList
            inventories={inventories}
            onSelect={setSelectedInventory}
            onDelete={deleteInventory}
            onCreateNew={() => setShowCreateModal(true)}
            onEditItemTypes={setEditingInventoryItemTypes}
          />
        ) : currentView === 'types' ? (
          <ItemTypeList
            itemTypes={itemTypes}
            onDelete={deleteItemType}
            onCreateNew={() => setShowCreateTypeModal(true)}
            onEdit={(itemType) => {
              setEditingItemType(itemType);
              setShowCreateTypeModal(true);
            }}
            onRefresh={fetchItemTypes}
          />
        ) : currentView === 'locations' ? (
          <LocationTypeList
            locationTypes={locationTypes}
            onDelete={deleteLocationType}
            onCreateNew={() => setShowCreateLocationTypeModal(true)}
            onEdit={(locationType) => {
              setEditingLocationType(locationType);
              setShowCreateLocationTypeModal(true);
            }}
            onRefresh={fetchLocationTypes}
          />
        ) : currentView === 'backlog' ? (
          <BacklogList />
        ) : currentView === 'ai-config' ? (
          <AIConfiguration />
        ) : currentView === 'completion-agents' ? (
          <CompletionAgentConfiguration />
        ) : null}

        {showCreateModal && (
          <CreateInventoryModal
            onClose={() => setShowCreateModal(false)}
            onCreate={createInventory}
          />
        )}

        {showCreateTypeModal && (
          <CreateItemTypeModal
            onClose={() => {
              setShowCreateTypeModal(false);
              setEditingItemType(null);
            }}
            onCreate={handleItemTypeAction}
            itemType={editingItemType}
          />
        )}

        {showCreateLocationTypeModal && (
          <CreateLocationTypeModal
            onClose={() => {
              setShowCreateLocationTypeModal(false);
              setEditingLocationType(null);
            }}
            onCreate={handleLocationTypeAction}
            locationType={editingLocationType}
          />
        )}

        {editingInventoryItemTypes && (
          <EditInventoryItemTypesModal
            inventory={editingInventoryItemTypes}
            onClose={() => setEditingInventoryItemTypes(null)}
            onUpdate={(updatedInventory) => {
              setInventories(inventories.map(inv => 
                inv.id === updatedInventory.id ? updatedInventory : inv
              ));
              setEditingInventoryItemTypes(null);
            }}
          />
        )}
      </div>
    </div>
  );
}

export default App;